import { cn } from '@/lib';
import type { HTMLAttributes, ReactNode } from 'react';

export interface InfoGridProps extends HTMLAttributes<HTMLDListElement> {
  children: ReactNode;
}

export interface InfoRowProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
}

/**
 * @component InfoGrid
 * @description Renders a responsive definition-list grid for read-only metadata values.
 *
 * @props
 *   children (ReactNode, required) - InfoRow elements or compatible definition-list content.
 *   className (string, optional) - Additional classes for the grid element.
 *
 * @slots
 *   children - Read-only value rows rendered inside the definition list.
 *
 * @changelog
 *   2026-06-26 - Initial creation from the device overview details grid.
 */
export function InfoGrid({ children, className, ...props }: InfoGridProps) {
  return (
    <dl
      className={cn(
        'grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2',
        className
      )}
      {...props}
    >
      {children}
    </dl>
  );
}

/**
 * @component InfoRow
 * @description Renders one labelled read-only value within an InfoGrid.
 *
 * @props
 *   label (string, required) - Label displayed above the value.
 *   value (ReactNode, optional) - Value displayed under the label when children are not provided.
 *   children (ReactNode, optional) - Custom value content displayed under the label.
 *   className (string, optional) - Additional classes for the row wrapper.
 *
 * @slots
 *   children - Custom value content, such as badges or formatted fragments.
 *
 * @changelog
 *   2026-06-26 - Initial creation from the device overview InfoRow.
 */
export function InfoRow({
  label,
  value,
  children,
  className,
  ...props
}: InfoRowProps) {
  const content = children ?? value ?? '-';

  return (
    <div className={cn('flex flex-col', className)} {...props}>
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-onsurface text-sm">{content}</dd>
    </div>
  );
}
